import React, { FC, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { AppContext } from 'contexts/AppProvider'
import { displayPrice } from 'helpers'
import Fonts from 'components/base-ui/Fonts'
import { BtnCircleV2, Icon, QuantitySwitch } from 'components/base-ui'
import { ThemeColor } from 'types/color'
import media from 'styles/media'
import { useTranslation } from 'react-i18next'
import { getResizableURL } from 'helpers/image'

interface ModifierCartItem {
  name: string
  priceSatangs: number
  quantity: number
  multipleQuantity: boolean
}

export interface ProductCartItem {
  images: {
    resizableURL: string
  }[]
  name: string
  modifiers: ModifierCartItem[]
  totalPriceSatangs: number
  quantity: number
  excludedFromPromotion: boolean
  customNotes: string
}
interface Props {
  product: ProductCartItem
  enabledActions?: boolean
  isShowQuantity?: boolean
  onUpdateQuantity?: (quantity: number) => void
  onClickEdit?: (product: ProductCartItem) => void
  onClickDelete?: (product: ProductCartItem) => void
  onClickItem: VoidFunction
  isInactive: boolean
  hideZeroPrice?: boolean
}

const { screenXsMax } = media

const CartItem: FC<Props> = ({
  product,
  enabledActions = true,
  isShowQuantity = false,
  onUpdateQuantity = (quantity) => {},
  onClickEdit = (product) => {},
  onClickDelete = (product) => {},
  onClickItem,
  isInactive,
  hideZeroPrice = false,
}) => {
  const { isThaiLang } = useContext(AppContext)
  const { t } = useTranslation()

  const modifiers = useMemo(() => {
    return product.modifiers
      .map(({ name, quantity, multipleQuantity }) => {
        return multipleQuantity ? `${name} ${quantity}X` : name
      })
      .join(', ')
  }, [isThaiLang, product.modifiers])

  const totalPrice = useMemo(() => {
    const productPrice = enabledActions
      ? (product.totalPriceSatangs / 100) * product.quantity
      : product.totalPriceSatangs / 100
    const modifiersPrice = product.modifiers.reduce(
      (currentPrice, { priceSatangs, quantity }) =>
        (currentPrice += (priceSatangs / 100) * quantity * product.quantity),
      0
    )
    return modifiersPrice + productPrice
  }, [product, enabledActions])

  const productName = isShowQuantity ? `${product.quantity}X ${product.name}` : product.name
  const showProductPrice = !(hideZeroPrice && totalPrice === 0)
  const image1x = getResizableURL(product.images, { width: '250', height: '250', formatType: '_fmt-jpg' })
  const image2x = getResizableURL(product.images, { width: '330', height: '330', formatType: '_fmt-jpg' })
  const image3x = getResizableURL(product.images, { width: '375', height: '375', formatType: '_fmt-jpg' })

  return (
    <Wrapper isInactive={isInactive}>
      <ProductThumbnailWrapper className="thumnail-wrapper">
        <ProductThumbnail
          className="thumnail"
          image1x={image1x}
          image2x={image2x}
          image3x={image3x}
          onClick={onClickItem}
        />
      </ProductThumbnailWrapper>
      <ProductDetails style={{ justifyContent: enabledActions ? 'space-between' : 'flex-start' }}>
        <Title>
          <ProductName className="product-name" title={productName} fullWidth={!showProductPrice} onClick={onClickItem}>
            {productName}
          </ProductName>
          {showProductPrice && <Price className="product-price">฿{displayPrice(totalPrice)}</Price>}
        </Title>
        {modifiers.length > 0 && <Modifiers className="product-modifiers">{modifiers}</Modifiers>}
        {product.customNotes && (
          <CustomNote className="product-customer-notes">{`${t('remark')} : ${product.customNotes}`}</CustomNote>
        )}
        {product.excludedFromPromotion && (
          <LabelExcludedPromotion className="label-excludeed-promotion">
            {t('productExcludedPromotionShort')}
          </LabelExcludedPromotion>
        )}

        {enabledActions && (
          <ActionWrapper data-testid="action-item">
            <QuantitySwitch
              testId={productName}
              color={ThemeColor.primary}
              xlg
              minValue={1}
              quantity={product.quantity}
              onUpdate={(quantity: number) => onUpdateQuantity(quantity)}
              disabledIncreasing={isInactive}
              disabled={isInactive}
            />
            <RightButtonWrapper>
              <BtnCircleV2
                data-testid={`${productName}EditBtn`}
                className="button update-product-modifier-btn"
                onClick={() => onClickEdit(product)}
                fill="outlined"
                color="monochrome"
                size="medium"
                icon={<Icon name="pencil-outline" />}
              />
              <BtnCircleV2
                data-testid={`${productName}DeleteBtn`}
                className="button delete-product-modifier-btn"
                onClick={() => onClickDelete(product)}
                fill="outlined"
                color="monochrome"
                size="medium"
                icon={<Icon name="bin" />}
              />
            </RightButtonWrapper>
          </ActionWrapper>
        )}
      </ProductDetails>
    </Wrapper>
  )
}

export default CartItem

const Wrapper = styled.div<{ isInactive: boolean }>`
  display: flex;
  align-items: flex-start;
  column-gap: 16px;

  ${({ isInactive, theme }) =>
    isInactive &&
    `
    .product-name, .product-price, .product-modifiers, .label-excludeed-promotion {
      color: ${theme.colors.baseGrey};
    }

    .thumnail-wrapper {
      background: rgba(85,85,85,0.5);
    }

    .thumnail{
      opacity: 0.5;
    }
  `}

  @media (${screenXsMax}) {
    column-gap: 8px;
    animation: fadeIn 1s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      visibility: hidden;
    }

    to {
      opacity: 1;
      visibility: visible;
    }
  }
`

const Title = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProductName = styled(Fonts.BaseTitleBold)<{ fullWidth: boolean }>`
  width: calc(100vw - 230px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (${screenXsMax}) {
    font-size: 12px;
    width: calc(100vw - 175px);
  }

  ${({ fullWidth }) =>
    fullWidth &&
    `
   flex: 1;
  `}
`

const Price = styled(Fonts.BaseTitleBold)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 5px;
  @media (${screenXsMax}) {
    font-size: 12px;
  }
`

const ProductThumbnailWrapper = styled.div`
  border-radius: 8px;
`
const ProductThumbnail = styled.div<{ image1x: string; image2x: string; image3x: string }>`
  width: 90px;
  height: 90px;
  background-size: contain;
  border-radius: 8px;
  background-image: -webkit-image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  background-image: image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  @media (${screenXsMax}) {
    width: 65px;
    height: 65px;
  }
`
const ProductDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 8px;
  width: 100%;
  min-height: 90px;

  @media (${screenXsMax}) {
    min-height: 65px;
  }
`

const Modifiers = styled(Fonts.SmallTitleRegular)`
  width: 75%;
`

const CustomNote = styled(Modifiers)`
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 2;
  box-orient: vertical;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const RightButtonWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`

const LabelExcludedPromotion = styled(Fonts.SmallTitleMedium)`
  color: ${(props) => props.theme.colors.infoDark};
  @media (${screenXsMax}) {
    font-size: 10px;
  }
`
